import { AccordionContext } from '@contexts/accordion'
import { AssetOptionsContext } from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import ContentfulContentArticleSummaryAccordionItem, {
  summaryAccordionAssetOptions,
} from '@entries/contentful-content-article-summary-accordion-item'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import { AccordionList } from '@ui/accordion-list'
import { graphql } from 'gatsby'
import React, { useCallback, useEffect, useState } from 'react'

const NO_SELECTION = ''

const ContentfulPageSectionSummaryAccordion: Utils.EntryFC<
  Queries.ContentfulPageSectionSummaryAccordionFragment
> = ({ content: { contents = [] } = {}, fieldInspectorProps }) => {
  const [activeContent, setActiveContent] = useState<
    Utils.UndefOrNull<Queries.ContentfulContentArticleSummaryAccordionItemFragment>
  >(contents[0] || null)
  const [currentSelected, setCurrentSelected] = useState(activeContent?.id)

  const onAccordionChanges = useCallback(
    (id?: string) => {
      if (id) {
        setActiveContent(contents.find((content) => content.id === id))
      }
    },
    [contents]
  )

  useEffect(() => {
    onAccordionChanges(currentSelected)
  }, [currentSelected])

  const onHeaderClick = useCallback(
    (id: string) => {
      setCurrentSelected(id === currentSelected ? NO_SELECTION : id)
    },
    [currentSelected]
  )

  return (
    <div className="grid gap-5 laptop:grid-cols-2">
      <div
        className="hidden items-start laptop:flex"
        {...fieldInspectorProps({
          entryId: activeContent?.contentful_id,
          fieldId: 'image',
        })}
      >
        {activeContent?.image && (
          <AssetOptionsContext.Provider value={summaryAccordionAssetOptions}>
            <Entry
              content={activeContent.image}
              mapping={{
                [ContentType.ContentfulAsset]: ContentfulAsset,
              }}
            />
          </AssetOptionsContext.Provider>
        )}
      </div>
      {contents && (
        <div
          className="flex items-stretch"
          {...fieldInspectorProps({
            fieldId: 'contents',
          })}
        >
          <AccordionContext.Provider
            value={{
              selectedId: currentSelected,
              setSelectedId: onHeaderClick,
            }}
          >
            <AccordionList>
              {contents.map((item, i) => (
                <Entry
                  key={i}
                  content={item}
                  mapping={{
                    [ContentType.ContentfulContentArticle]:
                      ContentfulContentArticleSummaryAccordionItem,
                  }}
                />
              ))}
            </AccordionList>
          </AccordionContext.Provider>
        </div>
      )}
    </div>
  )
}

export const query = graphql`
  fragment ContentfulPageSectionSummaryAccordion on ContentfulSection {
    ...ContentfulEntry
    contents {
      ...ContentfulContentArticleSummaryAccordionItem
    }
  }
`

export default withDisplayName(
  ContentfulPageSectionSummaryAccordion,
  'ContentfulPageSectionSummaryAccordion'
)
